import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const content = {
    en: {
        headerTitle: 'Our Services',
        headerSubtitle: 'Comprehensive solutions tailored to your business goals',
    },
    ar: {
        headerTitle: 'خدماتنا',
        headerSubtitle: 'حلول شاملة مصمّمة خصيصاً لتناسب أهداف عملك',
    },
};

const Services = () => {
    const { language } = useLanguage();
    const [selectedService, setSelectedService] = useState(0);
    const [handwritingProgress, setHandwritingProgress] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const cardsContainerRef = useRef(null);
    const t = content[language] || content.en;

    const services = [
        {
            title: 'Corporate Branding',
            arTitle: 'بناء وتصميم العلامات التجارية',
            description: 'Brand values, story and tone of voice',
            details: [
                'Brand strategy and positioning',
                'Logo and identity design',
                'Brand marks and elements',
                'Brand launch and implementation',
                'Brand copywriting'
            ],
            // --- ADDED: Arabic Details ---
            arDetails: [
                'استراتيجية العلامة التجارية وتحديد موقعها',
                'تصميم الشعار والهوية',
                'علامات وعناصر العلامة التجارية',
                'إطلاق العلامة التجارية وتطبيقها',
                'كتابة محتوى العلامة التجارية'
            ],
            color: '#6366f1',
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="w-8 h-8"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 17h14l1-10-4 3-4-6-4 6-4-3 1 10z"
                    />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 20h16"
                    />
                </svg>




            )
        },
        {
            title: 'Graphic Design Solutions',
            arTitle: 'تصميم الإعلانات والهوية البصرية',
            description: 'Visual identity and advertising design',
            details: [
                'Logo design',
                'Identity kit creation',
                'Storyboard creation',
                'Menu and printable items',
                'Social media branding',
                'Ads and content posts'
            ],
            arDetails: [
                'تصميم الشعار',
                'إنشاء حزمة الهوية البصرية',
                'إنشاء لوحة القصة (ستوري بورد)',
                'تصميم قائمة الطعام والمطبوعات',
                'هوية وسائل التواصل الاجتماعي',
                'الإعلانات ومحتوى المنشورات'
            ],
            color: '#10b981',
            icon: (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
            )
        },
        {
            title: 'Digital Marketing',
            arTitle: 'إدارة التسويق والإعلان',
            description: 'Marketing management and advertising',
            details: [
                'Social media management',
                'Content creation',
                'Promoting campaigns',
                'Google Ads & SEO solutions'
            ],
            arDetails: [
                'إدارة وسائل التواصل الاجتماعي',
                'صناعة المحتوى',
                'ترويج الحملات الإعلانية',
                'حلول إعلانات جوجل وتحسين محركات البحث'
            ],
            color: '#f59e0b',
            icon: (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            )
        },
        {
            title: 'Web Development',
            arTitle: 'بناء المواقع الإلكترونية',
            description: 'Website and application building',
            details: [
                'Domain & hosting registration',
                'UI/UX design',
                'Mobile app development',
                'E-commerce solutions',
                'API services',
                'QR code solutions'
            ],
            arDetails: [
                'تسجيل النطاق والاستضافة',
                'تصميم واجهة وتجربة المستخدم',
                'تطوير تطبيقات الجوال',
                'حلول التجارة الإلكترونية',
                'خدمات واجهة برمجة التطبيقات (API)',
                'حلول رمز الاستجابة السريعة (QR)'
            ],
            color: '#3b82f6',
            icon: (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
            )
        },
        {
            title: 'Media Production',
            arTitle: 'الإنتاج الفني والإعلاني',
            description: 'Artistic and advertising production',
            details: [
                'Script writing & shot lists',
                'Photography services',
                'Videography services',
                'TVC production',
                '2D & 3D animation',
                'AI services'
            ],
            arDetails: [
                'كتابة السيناريو وقوائم التصوير',
                'خدمات التصوير الفوتوغرافي',
                'خدمات تصوير الفيديو',
                'إنتاج الإعلانات التلفزيونية',
                'الرسوم المتحركة ثنائية وثلاثية الأبعاد',
                'خدمات الذكاء الاصطناعي'
            ],
            color: '#ec4899',
            icon: (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
            )
        }
    ];

    useEffect(() => {
        if (isAnimating) {
            const t = setTimeout(() => {
                if (handwritingProgress < 100) {
                    setHandwritingProgress(p => p + 5);
                } else {
                    setIsAnimating(false);
                }
            }, 30);
            return () => clearTimeout(t);
        }
    }, [handwritingProgress, isAnimating]);

    const handleServiceSelect = idx => {
        setSelectedService(idx);
        setHandwritingProgress(0);
        setIsAnimating(true);
        if (window.innerWidth < 768 && cardsContainerRef.current) {
            const card = cardsContainerRef.current.children[idx];
            const cw = cardsContainerRef.current.offsetWidth;
            if (card) {
                cardsContainerRef.current.scrollTo({
                    left: card.offsetLeft - (cw - card.offsetWidth) / 2,
                    behavior: 'smooth'
                });
            }
        }
    };

    const createBubbles = () =>
        Array.from({ length: 15 }).map((_, i) => (
            <motion.div
                key={i}
                className="absolute rounded-full bg-blue-200/30"
                style={{ top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`, width: `${Math.random() * 40 + 10}px`, height: `${Math.random() * 40 + 10}px` }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 0.5, 0], scale: [0, 1, 0], y: [0, -100] }}
                transition={{ duration: 2 + Math.random() * 3, delay: Math.random(), repeat: Infinity, repeatDelay: Math.random() * 5 }}
            />
        ));

    const displayTitle = language === 'ar' ? services[selectedService].arTitle : services[selectedService].title;

    // --- CHOOSING THE CORRECT DETAILS LIST ---
    const displayDetails = language === 'ar'
        ? services[selectedService].arDetails
        : services[selectedService].details;

    return (
        <section
            id="services"
            className="relative py-16 md:pl-48 bg-light overflow-hidden"
            dir={language === 'ar' ? 'rtl' : 'ltr'}
        >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">{createBubbles()}</div>
            <div className="container mx-auto px-4 relative z-10">
                <motion.div className="text-center mb-12 max-w-3xl mx-auto" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                    <motion.h2 className="text-4xl md:text-5xl font-greycliff font-bold mb-4 text-neutral-900" initial={{ scale: 0.9 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
                        <span className="bg-clip-text text-neutral-900">{t.headerTitle}</span>
                    </motion.h2>
                    {/*<motion.div className="h-1 w-16 bg-gradient-to-r from-indigo-500 to-blue-500 mx-auto rounded-full mb-6" initial={{ width: 0 }} animate={{ width: '4rem' }} transition={{ delay: 0.4, duration: 0.8 }} />*/}
                    <motion.p className="text-gray-600" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>{t.headerSubtitle}</motion.p>
                </motion.div>

                <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
                    <motion.div className="md:w-2/5 flex-shrink-0" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
                        <div ref={cardsContainerRef} className="flex overflow-x-auto pb-4 space-x-4 md:block md:space-y-4 hide-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {services.map((svc, i) => (
                                <motion.div
                                    key={i}
                                    className={`relative rounded-xl p-4 cursor-pointer transition-all flex-shrink-0 w-40 md:w-auto ${ selectedService === i ? 'bg-white shadow-lg border-l-4' : 'bg-white/80 hover:bg-white shadow-sm' }`}
                                    style={{ borderLeftColor: selectedService === i ? svc.color : 'transparent', transform: selectedService === i ? 'translateX(5px)' : 'none' }}
                                    onClick={() => handleServiceSelect(i)}
                                    whileHover={{ y: -3 }}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 * i, duration: 0.5 }}
                                >
                                    <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                                        <div className="flex-1">
                                            <h3 className="font-greycliff font-bold text-gray-900 text-sm md:text-base">{language === 'ar' ? svc.arTitle : svc.title}</h3>
                                            <p className="text-xs text-gray-500 line-clamp-1">{svc.description}</p>
                                        </div>
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${language === 'ar' ? 'ml-3' : 'mr-3'}`} style={{ backgroundColor: `${svc.color}20` }}>
                                            <motion.div whileHover={{ rotate: 10, scale: 1.1 }} transition={{ type: 'spring', stiffness: 300 }}>
                                                {svc.icon}
                                            </motion.div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div className="md:w-3/5" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.6 }}>
                        <div className="bg-white rounded-xl shadow-xl overflow-hidden h-full">
                            <div className="h-full flex flex-col">
                                <div className="h-12 md:h-14 flex items-center px-4 md:px-6 border-b" style={{ backgroundColor: `${services[selectedService].color}10` }}>
                                    <div className="flex gap-x-1.5 md:gap-x-2">
                                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-400" />
                                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-400" />
                                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-400" />
                                    </div>
                                </div>
                                <div className="flex-1 p-4 md:p-6 flex flex-col">
                                    <AnimatePresence mode="wait">
                                        <motion.div key={selectedService} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} className="flex-1">
                                            <div className="mb-4 md:mb-6 relative inline-block overflow-hidden whitespace-nowrap">
                                                <motion.h3 className="text-xl md:text-2xl font-greycliff font-bold text-gray-900 mb-1" initial={{ clipPath: 'inset(0 100% 0 0)' }} animate={{ clipPath: 'inset(0 0% 0 0)' }} transition={{ duration: 1.5 }}>
                                                    {displayTitle}
                                                </motion.h3>
                                                <motion.div className="absolute bottom-0 left-0 h-0.5 md:h-1 rounded-full" style={{ backgroundColor: services[selectedService].color }} initial={{ width: 0 }} animate={{ width: `${handwritingProgress}%` }} transition={{ duration: 1.5 }}/>
                                            </div>

                                            {/* --- UPDATED: Rendering dynamic details list --- */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mb-4 md:mb-6">
                                                {displayDetails.map((d, i) => (
                                                    <motion.div
                                                        key={i}
                                                        className="flex items-start py-1"
                                                        initial={{ opacity: 0, x: language === 'ar' ? 10 : -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: 0.1 * i + 0.5 }}
                                                    >
                                                        <div className={`flex-shrink-0 mt-1.5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`}>
                                                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: services[selectedService].color }}/>
                                                        </div>
                                                        <span className="text-xs md:text-sm text-gray-700">{d}</span>
                                                    </motion.div>
                                                ))}
                                            </div>

                                            <div className="mt-2 md:mt-4">
                                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                                    <div className="flex justify-center">
                                                        <div className="relative">
                                                            <motion.div
                                                                className="w-32 h-32 md:w-40 md:h-40 rounded-full flex items-center justify-center mx-auto"
                                                                style={{ backgroundColor: `${services[selectedService].color}10`, border: `2px dashed ${services[selectedService].color}`}}
                                                                animate={{ rotate: 360, scale: [1, 1.1, 1] }}
                                                                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                                                            >
                                                                <motion.div
                                                                    className="w-20 h-20 md:w-28 md:h-28 rounded-full flex items-center justify-center"
                                                                    style={{ backgroundColor: `${services[selectedService].color}20` }}
                                                                    animate={{ rotate: -360, scale: [1, 0.9, 1] }}
                                                                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                                                                >
                                                                    <motion.div className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: services[selectedService].color }} animate={{ rotate: [0, 10, 0], scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}>
                                                                        {services[selectedService].icon}
                                                                    </motion.div>
                                                                </motion.div>
                                                            </motion.div>
                                                            {[...Array(4)].map((_, i) => (
                                                                <motion.div
                                                                    key={i}
                                                                    className="absolute w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white shadow-md"
                                                                    style={{ backgroundColor: services[selectedService].color, top: i % 2 === 0 ? '10%' : '70%', left: i < 2 ? '0%' : '80%' }}
                                                                    animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
                                                                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}
                                                                >
                                                                    {services[selectedService].icon}
                                                                </motion.div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Services;